import React, { useState, useMemo, useEffect } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import filter from 'lodash/filter';
import forEach from 'lodash/forEach';
import isEqual from 'lodash/isEqual';
import map from 'lodash/map';
import noop from 'lodash/noop';
import orderBy from 'lodash/orderBy';
import PropTypes from 'prop-types';
import some from 'lodash/some';
import styled from 'styled-components';

import Checkbox from './checkbox';
import DropdownMenu, { MenuItem, OverrideMenuItem } from 'components/common/dropdown_menu_v2';
import PopoverMenu from 'components/common/menu';

/**
 * @visibleName Multi Select Dropdown Menu
 */

export default function MultiSelectDropdownMenu({
  className = 'dropdown-menu',
  entityNamePlural = 'Items',
  fitOpenerWidth = true,
  onSelect,
  options,
  values = [],
  getDropdownLabel,
  selectedOptionsFirst = true,
  ...otherProps
}) {
  const [needToRearrange, setNeedToRearrange] = useState(true);
  const [arrangedOptions, setArrangedOptions] = useState([]);

  const sortedOptions = useMemo(() => sortedHierarchialOptions(options), [options]);
  const [priorOptions, setPriorOptions] = useState(sortedOptions);

  useEffect(() => {
    // Rearrange options if changes
    if (!isEqual(sortedOptions, priorOptions)) {
      setPriorOptions(sortedOptions);
      setNeedToRearrange(true);
    }
  }, [sortedOptions, priorOptions, setPriorOptions, setNeedToRearrange]);

  useEffect(() => {
    if (!needToRearrange) {
      return;
    }
    setNeedToRearrange(false);

    if (!selectedOptionsFirst) {
      setArrangedOptions(sortedOptions);
      return;
    }

    const checkedOptions = getFilteredOptions({ items: sortedOptions, values, checked: true });
    const uncheckedOptions = getFilteredOptions({ items: sortedOptions, values, checked: false });

    const updatedArrangedOptions = [...checkedOptions, ...uncheckedOptions];
    setArrangedOptions(updatedArrangedOptions);
  }, [needToRearrange, setNeedToRearrange, setArrangedOptions, selectedOptionsFirst, sortedOptions, values]);

  const handleSelect = value => {
    onSelect && onSelect(values.includes(value) ? filter(values, val => val !== value) : [...values, value]);
  };

  const handleClearAll = () => {
    onSelect && onSelect([]);
  };

  const handlePopupClose = () => {
    setNeedToRearrange(true);
  };

  const allFlatList = useMemo(() => getFlatItemList({ options: sortedOptions, values, allItems: true }), [
    sortedOptions,
    values,
  ]);
  const selectedFlatList = useMemo(() => getFlatItemList({ options: sortedOptions, values }), [sortedOptions, values]);

  const renderItems = items => {
    return <GroupItems handleSelect={handleSelect} items={items} selected={selectedFlatList} />;
  };

  const customText = getDropdownLabel
    ? () => getDropdownLabel(selectedFlatList, otherProps?.placeholder)
    : () => getDefaultDropdownLabel(selectedFlatList, otherProps?.placeholder);
  const tooltip = getTooltip(selectedFlatList);

  const footer = selectedFlatList.length === allFlatList.length ? 'All items selected' : null;

  const getSelectedInfoText = selectedOptions => {
    const numberText = selectedOptions.length ? selectedOptions.length : 'None';

    return `${numberText} ${entityNamePlural} selected`;
  };

  return (
    <DropdownMenu
      className={className}
      customFilterFunc={filterFn}
      customItemsRenderer={renderItems}
      customText={customText}
      fitOpenerWidth={fitOpenerWidth}
      footer={footer}
      isMultiSelect
      menuComponent={StyledPopoverMenu}
      onClearAll={handleClearAll}
      onPopupClose={handlePopupClose}
      onSelect={noop}
      options={arrangedOptions}
      renderSelectedCount={getSelectedInfoText}
      searchable
      tooltipMessage={tooltip}
      value={values}
      {...otherProps}
    />
  );
}

function filterFn(searchText, items) {
  return filterOptions({
    items,
    filterFn: ({ label, value, text }) =>
      some([label, value, text], value => value?.toLowerCase()?.includes(searchText.toLowerCase())),
  });
}

function getFlatItemList({ options, values, allItems = false }) {
  let groupedItems = [];
  forEach(options, item => {
    if (item.type === 'group') {
      const children = getFlatItemList({ options: item.items, values, allItems });
      if (children.length) {
        groupedItems.push(...children);
      }
    } else if (!item.type) {
      if (allItems) {
        groupedItems.push(item);
      } else if (values?.includes(item.value)) {
        groupedItems.push(item);
      }
    }
  });
  return groupedItems;
}

function filterOptions({ items: options, filterFn }) {
  const groupedItems = [];

  forEach(options, item => {
    if (item.type === 'group') {
      const filteredChildren = filterOptions({ items: item.items, filterFn });

      if (filteredChildren.length) {
        groupedItems.push({
          ...item,
          value: `group_${item.label}_${Math.random()}`,
          items: filteredChildren,
        });
      }
    } else if (filterFn(item)) {
      groupedItems.push(item);
    }
  });
  return groupedItems;
}

function getFilteredOptions({ items, values, checked }) {
  const filterFn = option => {
    return checked ? values.includes(option.value) : !values.includes(option.value);
  };
  const clonedItems = cloneDeep(items);
  return filterOptions({ items: clonedItems, values, filterFn });
}

function GroupItems({ items, selected, handleSelect, level = 1 }) {
  let groupedItems = [];
  forEach(items, item => {
    if (item.type === 'group') {
      const children = GroupItems({ items: item.items, selected, handleSelect, level: level + 1 });
      if (children.length) {
        groupedItems.push(
          <Item key={`header-${item.label}-level-${level}-${item.value}`} level={level} option={item} type="group" />
        );
        groupedItems.push(...children);
      }
    } else {
      groupedItems.push(
        <Item
          data-aid={`option-${item.value}`}
          handleSelect={handleSelect}
          key={item.value}
          option={item}
          selected={selected}
        />
      );
    }
  });
  return groupedItems;
}

export const Item = ({ type, ...otherProps }) => {
  switch (type) {
    case 'group': {
      return <GroupHeader data-aid={`header-level-${otherProps.level}`}>{otherProps.option.label}</GroupHeader>;
    }
    default: {
      return (
        <Option
          data-aid={`option-${otherProps.option.value}`}
          handleSelect={otherProps.handleSelect}
          option={otherProps.option}
          selected={otherProps.selected}
        />
      );
    }
  }
};

export function Option({ option, selected, handleSelect }) {
  const isFocused = selected.some(({ value }) => option.value === value);
  return (
    <MenuItem
      className="menuOption"
      data-aid={`menuOption-${option.value}`}
      isFocused={isFocused}
      isMultiSelect
      onClose={noop}
      onSelect={handleSelect}
      option={option}
      renderCustomContent={option => (
        <div>
          <Checkbox
            checked={!!isFocused}
            data-aid={`checkbox-${option.value}`}
            label={option.label}
            onChange={event => event.stopPropagation()}
            value={(!!isFocused).toString()}
          />
        </div>
      )}
    />
  );
}

const TooltipMaxItemsCount = 10;

function getTooltip(selected) {
  let items = map(selected, item => item.label);

  if (selected.length > TooltipMaxItemsCount) {
    items = [...items.slice(0, TooltipMaxItemsCount), `+ ${selected.length - TooltipMaxItemsCount} more `];
  }
  return items.join('\n');
}

function getDefaultDropdownLabel(selected, placeholder) {
  switch (selected.length) {
    case 0:
      return placeholder || 'None';
    case 1:
      return `${selected[0].label}`;
    case 2:
      return `${selected[0].label} and ${selected[1].label}`;
    default:
      return `${selected[0].label} and +${selected.length - 1} more`;
  }
}

function sortedHierarchialOptions(options) {
  const sorted = orderBy(options, ['label'], ['acs']);
  return map(
    sorted,
    option => {
      if (option.type === 'group') {
        option.items = [...sortedHierarchialOptions(option.items)];
      }

      return option;
    },
    []
  );
}

MultiSelectDropdownMenu.propTypes = {
  /** Sets the support of an empty value as allowed option value, e.g. { value: '', label: 'None' } */
  allowEmptyValues: PropTypes.bool,
  className: PropTypes.string,
  'data-aid': PropTypes.string,
  disabled: PropTypes.bool,
  fitOpenerWidth: PropTypes.bool,
  /** Set the maximum height by setting a maximum number of rows with a number of pixel bleed over */
  maxRows: PropTypes.number,
  maxHeight: PropTypes.number,
  maxWidth: PropTypes.number,
  onSelect: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.exact({
        label: PropTypes.node.isRequired,
        type: PropTypes.oneOf(['HEADER', 'group']).isRequired,
        items: PropTypes.any,
      }),
      PropTypes.exact({
        label: PropTypes.node.isRequired,
        text: PropTypes.string, // Text search (specified via searchable prop) will use this vs. value if specified
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).isRequired,
      }),
    ])
  ),
  placeholder: PropTypes.string, // Placeholder text for collapsed dropdown menu
  readOnly: PropTypes.bool,
  searchPlaceholder: PropTypes.string, // Placeholder text for search input if searchable is true
  searchable: PropTypes.bool,
  text: PropTypes.node,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool, PropTypes.array]),
  getDropdownLabel: PropTypes.func,
};

export const SectionSplitter = styled.div`
  border-bottom: 1px solid ${p => p.theme.colors.gray300};
  height: 1px;
`;

export const GroupHeader = styled.div`
  padding: 8px 16px;
`;

const StyledPopoverMenu = styled(PopoverMenu)`
  overflow: inherit;

  ${OverrideMenuItem} {
    padding: 8px 16px;
  }
`;
